// frontend/src/components/common/ErrorBoundary.jsx

import React from "react";

/**
 * ErrorBoundary — Catches JavaScript errors in child components
 *
 * React Error Boundaries catch errors during rendering, in lifecycle methods,
 * and in constructors of child components. They prevent the entire application
 * from crashing when a single component throws an error.
 *
 * This is a critical safety net for the ManejaRD application. Without it,
 * a single JavaScript error (like accessing a property on undefined) could
 * render the entire page blank, leaving users confused with no way to recover.
 *
 * How it works:
 * 1. A child component throws an error during render
 * 2. React catches the error and calls getDerivedStateFromError()
 * 3. The ErrorBoundary sets hasError=true and renders the fallback UI
 * 4. The rest of the application continues to function normally
 * 5. User can click "Recargar" to try again
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComplexComponent thatMightError={data} />
 *   </ErrorBoundary>
 *
 * You can wrap entire pages, sections, or individual components.
 * The more granular the boundary, the less impact an error has.
 *
 * For the admin dashboard, we recommend:
 * - One ErrorBoundary around the entire admin layout
 * - One ErrorBoundary around each major section (ChatQueue, Appointments table, etc.)
 * - One ErrorBoundary around chart components (common source of errors)
 *
 * Props:
 * @param {React.ReactNode} children - Child components to wrap
 * @param {React.ReactNode} fallback - Custom fallback UI (overrides default)
 * @param {Function} onError - Custom error handler (for logging services)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    // Track whether an error has been caught
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  /**
   * Called when a child component throws an error
   *
   * This is a static lifecycle method that derives new state from the error.
   * We use it to set hasError=true so the fallback UI renders instead.
   *
   * @param {Error} error - The error that was thrown
   * @returns {Object} - New state to set
   */
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after an error has been thrown by a child component
   *
   * This is where we log the error and call any custom error handler.
   * The errorInfo parameter contains the component stack trace,
   * which is extremely useful for debugging.
   *
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Object with componentStack property
   */
  componentDidCatch(error, errorInfo) {
    // Log the error to console for development debugging
    console.error("ErrorBoundary caught an error:", error);
    console.error("Component stack:", errorInfo.componentStack);

    // Update state with full error info
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error tracking service
    // like Sentry, Bugsnag, or Rollbar
    if (process.env.NODE_ENV === "production") {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      // For now, just log it
      try {
        fetch("/api/log-error/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            error: error.toString(),
            componentStack: errorInfo.componentStack,
            url: window.location.href,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {
          // Silently fail — we can't let error logging crash the app
        });
      } catch (e) {
        // Double safety — never let error handling cause more errors
      }
    }
  }

  /**
   * Reset the error state and try rendering children again
   *
   * This allows users to recover from transient errors without
   * refreshing the entire page. The component will re-render its
   * children, and if the error has been resolved (e.g., bad data
   * was replaced), everything will work normally.
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reload the entire page
   *
   * When the error persists even after reset, a full page reload
   * is the nuclear option that clears all component state.
   */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    // If a custom fallback was provided, use it
    if (this.state.hasError && this.props.fallback) {
      return this.props.fallback;
    }

    // If an error was caught, show the error UI
    if (this.state.hasError) {
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error Title */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Algo salió mal
            </h3>

            {/* Error Description */}
            <p className="text-sm text-gray-600 mb-6">
              Ocurrió un error inesperado al mostrar esta sección. Puedes
              intentar recargar o volver al inicio.
            </p>

            {/* Error Details (collapsible, for debugging) */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                  Detalles del error (solo desarrollo)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded-lg overflow-auto text-xs text-red-700 max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Intentar de Nuevo
              </button>

              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Recargar Página
              </button>
            </div>

            {/* Multiple errors warning */}
            {this.state.errorCount > 2 && (
              <p className="mt-4 text-xs text-amber-600">
                Este error ha ocurrido múltiples veces. Si persiste, contacta al
                soporte técnico.
              </p>
            )}
          </div>
        </div>
      );
    }

    // No error — render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
